/**
 * Parse the passed value to a number. This method was taken from the dist output of the npm package
 * parse-decimal-number. It was taken and added directly here because the original package was 2+ years
 * old and was written in coffeescript. Additionally, some bugs were found where tests for null were not
 * also checking for undefined causing parsing errors.
 * Taken from here: https://github.com/AndreasPizsa/parse-decimal-number/blob/master/dist/parse-decimal-number.js
 *
 * @param value The value to be parsed to a decimal number.
 * @param inOptions Options for parsing the value. The default thousands holder is `,` and the default
 * decimal placeholder is `.`. You can override this by passing a two-character string of
 * <thousands><decimal> or a two-entity array where index 0 is thousands and index 1 is decimal or an
 * object with two properties, thousands and decimal.
 * @param enforceGroupSize Flag to indicate if group-size should be enforced (for thousands). The default is true.
 * @returns number that is the parsed decimal number. If the value passed is not a valid number NaN is returned.
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014 Andreas Pizsa
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
export function ParseDecimalNumber(
  value: any,
  inOptions: any = null,
  enforceGroupSize = true
): number {
  if (typeof value === 'number') {
    return value
  }

  const groupMinSize =
    enforceGroupSize || enforceGroupSize === null || enforceGroupSize === undefined ? 3 : 1

  let decimal
  let thousands

  if (typeof inOptions === 'string') {
    if (inOptions.length !== 2) {
      throw {
        name: 'ArgumentException',
        message: "The format for string options is '<thousands><decimal>' (exactly two characters)"
      }
    }
    thousands = inOptions.charAt(0)
    decimal = inOptions.charAt(1)
  } else if (Array.isArray(inOptions)) {
    if (inOptions.length !== 2) {
      throw {
        name: 'ArgumentException',
        message: "The format for array options is '<thousands><decimal>' (exactly two elements)"
      }
    }
    thousands = inOptions[0]
    decimal = inOptions[1]
  } else {
    thousands = inOptions && inOptions.thousands ? inOptions.thousands : ','
    decimal = inOptions && inOptions.decimal ? inOptions.decimal : '.'
  }

  const pattern = new RegExp(
    '^\\s*([+-]?(?:(?:\\d{1,3}(?:\\' +
      thousands +
      '\\d{' +
      groupMinSize +
      ',3})+)|\\d*))(?:\\' +
      decimal +
      '(\\d*))?\\s*$'
  )
  try {
    const result = value.match(pattern)
    if (!(result !== null && result.length === 3)) {
      return 0 / 0
    }
    const integerPart = result[1].replace(new RegExp('\\' + thousands, 'g'), '')
    const fractionPart = result[2]
    return parseFloat(`${integerPart}.${fractionPart}`)
  } catch (e) {
    console.error('ParseDecimalNumber', e)
    return 0 / 0
  }
}
